import { useState, useEffect } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Users, Edit, Calendar } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DIAS_SEMANA = [
  { id: "segunda", label: "Segunda" },
  { id: "terca", label: "Terça" },
  { id: "quarta", label: "Quarta" },
  { id: "quinta", label: "Quinta" },
  { id: "sexta", label: "Sexta" },
  { id: "sabado", label: "Sábado" },
  { id: "domingo", label: "Domingo" },
];


export default function Turmas({ onLogout }) {
  const [turmas, setTurmas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    modalidade: "",
    faixa_etaria: "",
    dias_semana: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      
      const [turmasRes, alunosRes] = await Promise.all([
        api.get(`/turmas`),
        api.get(`/alunos`)
      ]);
      // Ordenar turmas alfabeticamente
      const turmasOrdenadas = turmasRes.data.sort((a, b) => 
        a.nome.localeCompare(b.nome, 'pt-BR')
      );
      setTurmas(turmasOrdenadas);
      
      // Ordenar alunos alfabeticamente
      const alunosOrdenados = alunosRes.data.sort((a, b) => 
        a.nome.localeCompare(b.nome, 'pt-BR')
      );
      setAlunos(alunosOrdenados);
    } catch (error) {
      toast.error("Erro ao carregar turmas");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      
      
      if (editingId) {
        // Editar turma existente
        await api.put(`/turmas/${editingId}`, formData);
        toast.success("Turma atualizada com sucesso!");
      } else {
        // Criar nova turma
        await api.post('/turmas', formData);
        toast.success("Turma criada com sucesso!");
      }
      
      setShowDialog(false);
      setEditingId(null);
      setFormData({ nome: "", modalidade: "", faixa_etaria: "", dias_semana: [] });
      fetchData();
    } catch (error) {
      toast.error(editingId ? "Erro ao atualizar turma" : "Erro ao criar turma");
    }
  };

  const handleEdit = (turma) => {
    setEditingId(turma.id);
    setFormData({
      nome: turma.nome,
      modalidade: turma.modalidade,
      faixa_etaria: turma.faixa_etaria,
      dias_semana: turma.dias_semana || []
    });
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingId(null);
    setFormData({ nome: "", modalidade: "", faixa_etaria: "", dias_semana: [] });
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/turmas/${deleteId}`);
      toast.success("Turma deletada com sucesso!");
      fetchData();
      setDeleteId(null);
    } catch (error) {
      toast.error("Erro ao deletar turma");
    }
  };

  const getAlunosPorTurma = (turmaId) => {
    return alunos.filter(aluno => {
      // Suporte a migração de dados antigos
      const turmaIds = aluno.turma_ids || (aluno.turma_id ? [aluno.turma_id] : []);
      return turmaIds.includes(turmaId);
    });
  };

  if (loading) {
    return <Layout onLogout={onLogout}><div className="text-center py-12">Carregando...</div></Layout>;
  }

  return (
    <Layout onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Turmas</h1>
            <p className="text-gray-600">Gerencie as turmas da escola</p>
          </div>
          <Button
            data-testid="nova-turma-button"
            onClick={() => setShowDialog(true)}
            className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 shadow-lg w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Turma
          </Button>
        </div>

        {turmas.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-gray-500" data-testid="empty-state">
                Nenhuma turma cadastrada
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {turmas.map((turma) => {
              const alunosTurma = getAlunosPorTurma(turma.id);
              return (
                <Card key={turma.id} className="hover:shadow-lg transition-shadow" data-testid={`turma-card-${turma.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg sm:text-xl mb-2 break-words" data-testid={`turma-nome-${turma.id}`}>{turma.nome}</CardTitle>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="secondary">{turma.modalidade}</Badge>
                          <Badge variant="outline">{turma.faixa_etaria}</Badge>
                        </div>
                        {turma.dias_semana && turma.dias_semana.length > 0 && (
                          <div className="flex items-center gap-1 mt-2 flex-wrap">
                            <Calendar className="w-3 h-3 text-gray-500" />
                            {turma.dias_semana.map(dia => (
                              <Badge key={dia} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                {DIAS_SEMANA.find(d => d.id === dia)?.label || dia}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 sm:gap-2 shrink-0">
                        <Button
                          data-testid={`edit-turma-${turma.id}`}
                          variant="outline"
                          size="icon"
                          onClick={() => handleEdit(turma)}
                          className="h-9 w-9"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-turma-${turma.id}`}
                          variant="outline"
                          size="icon"
                          onClick={() => setDeleteId(turma.id)}
                          className="text-red-600 hover:bg-red-50 h-9 w-9"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="w-4 h-4" />
                      <span className="text-sm" data-testid={`turma-alunos-count-${turma.id}`}>{alunosTurma.length} aluno(s)</span>
                    </div>
                    {alunosTurma.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {alunosTurma.slice(0, 3).map(aluno => (
                          <div key={aluno.id} className="text-sm text-gray-500">• {aluno.nome}</div>
                        ))}
                        {alunosTurma.length > 3 && (
                          <div className="text-sm text-gray-400">+{alunosTurma.length - 3} mais</div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog key="turma-form-dialog" open={showDialog} onOpenChange={handleCloseDialog}>
        <DialogContent data-testid="nova-turma-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Turma" : "Nova Turma"}</DialogTitle>
            <DialogDescription>{editingId ? "Atualize os dados da turma" : "Cadastre uma nova turma na escola"}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome da Turma *</Label>
              <Input
                id="nome"
                data-testid="turma-nome-input"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Turma A"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="modalidade">Modalidade *</Label>
              <Input
                id="modalidade"
                data-testid="turma-modalidade-input"
                value={formData.modalidade}
                onChange={(e) => setFormData({ ...formData, modalidade: e.target.value })}
                placeholder="Ex: Futebol, Vôlei, Basquete"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="faixa_etaria">Faixa Etária *</Label>
              <Input
                id="faixa_etaria"
                data-testid="turma-faixa-input"
                value={formData.faixa_etaria}
                onChange={(e) => setFormData({ ...formData, faixa_etaria: e.target.value })}
                placeholder="Ex: 6-8 anos"
                required
              />
            </div>
            <div className="space-y-3">
              <Label>Dias de Treino</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {DIAS_SEMANA.map((dia) => (
                  <div key={dia.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`dia-${dia.id}`}
                      data-testid={`dia-${dia.id}`}
                      checked={formData.dias_semana.includes(dia.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ 
                            ...formData, 
                            dias_semana: [...formData.dias_semana, dia.id] 
                          });
                        } else {
                          setFormData({ 
                            ...formData, 
                            dias_semana: formData.dias_semana.filter(d => d !== dia.id) 
                          });
                        }
                      }}
                    />
                    <label
                      htmlFor={`dia-${dia.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {dia.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog} data-testid="cancel-turma">
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-turma" className="bg-gradient-to-r from-green-500 to-blue-500">
                {editingId ? "Atualizar" : "Criar Turma"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-turma-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta turma? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-turma">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-turma" onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
